import {
  Card,
  Tag,
  Button,
  ConfigProvider,
  PopconfirmProps,
  Popconfirm,
  message,
  Flex,
} from "antd";
// @ts-expect-error: No types available for react-copy-to-clipboard
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  CheckCircleFilled,
  DeleteOutlined,
  FireOutlined,
  SaveFilled,
} from "@ant-design/icons";
import { CardTabListType } from "antd/es/card";
import { FirestoreCollections, metadata } from '@leedpulse-monorepo/firebase-metadata';

import styles from './FormCard.module.css';

const { projectId, databaseId } = metadata('backoffice');
const consoleDatabaseId = databaseId === '(default)' ? '-default-' : databaseId; // The Firebase console aliases the (default) database as -default- in its URLs
interface FormCardProps {
  title: string | React.ReactNode;
  collectionName?: FirestoreCollections; // Set it when title is and Id to allow firebase redirect button
  isTitleAnId?: boolean;
  showTitle?: boolean;
  actions?: React.ReactNode[];
  headerActions?: React.ReactNode[] | React.ReactNode;
  tabList?: CardTabListType[],
  activeTabKey?: string,
  disableDeleteButton?: boolean,
  isOnError?: boolean,
  onDeleted?: () => void;
  onUpdated?: () => void;
  onCreated?: () => void;
  onTabChange?: (key: string) => void;
  isDeleteLoading?: boolean;
  isUpdateLoading?: boolean;
  isCreateLoading?: boolean;
  styles?: { [key: string]: React.CSSProperties };
}

export const READ_ONLY_INPUT_STYLE = {
  cursor: "not-allowed",
  backgroundColor: "var(--background-color)",
  border: "none",
  width: "100%",
};

const FormCard: React.FC<React.PropsWithChildren<FormCardProps>> = ({
  children,
  title,
  collectionName = undefined,
  isTitleAnId = true,
  showTitle = true,
  actions,
  headerActions,
  tabList,
  activeTabKey,
  disableDeleteButton,
  isOnError,
  onDeleted,
  onUpdated,
  onCreated,
  onTabChange,
  isDeleteLoading,
  isUpdateLoading,
  isCreateLoading,
  styles: additionalStyles,
}) => {
  const confirm: PopconfirmProps["onConfirm"] = (_) => {
    onDeleted?.();
  };

  const handleOpenFirebaseConsole = (documentId: string, collectionName: FirestoreCollections) => {
    // TODO : Le jour ou on déplace tous les calls User côté back on pourra calculer cet url côté back de la même façon que pour les Company
    const firebaseUrl = `https://console.firebase.google.com/u/0/project/${projectId}/firestore/databases/${consoleDatabaseId}/data/~2F${collectionName}~2F${documentId}`;

    window.open(firebaseUrl, '_blank');
  };

  return (
    <Card
      title={
        showTitle ? (
          isTitleAnId ? (
            <Flex align="center" gap="8px">
              <CopyToClipboard
                text={title}
                onCopy={() => message.success("ID Copié")}
              >
                <div className={styles['detailFormCardTitle']}>
                  <Tag
                    bordered={true}
                    style={{
                      fontSize: '1em',
                      marginTop: '0.5em',
                      marginBottom: '0.5em',
                    }}>
                    {title}
                  </Tag>
                </div>
              </CopyToClipboard>

              {collectionName && <Button
                className={styles['linkButton']}
                size='small'
                type="link"
                icon={<FireOutlined />}
                onClick={() => handleOpenFirebaseConsole(title as string, collectionName)}
              >
                Voir
              </Button>}
            </Flex>
          ) : (
            <p style={{ fontSize: '1.2em', marginBlock: 20 }}>{title}</p>
          )
        ) : null
      }
      actions={actions}
      extra={
        <ConfigProvider
          theme={{
            token: {
              controlHeight: 32,
            },
            components: {
              Button: {
                fontWeight: 500,
                paddingContentHorizontal: 16,
              },
            },
          }}
        >
          <Flex gap="0.5rem" align="center">
            {headerActions}
            {onDeleted && (
              <Popconfirm
                title="Attention !"
                description="Supprimer le document ?"
                onConfirm={confirm}
                okText="Oui"
                cancelText="Non"
              >
                <Button
                  type="default"
                  danger
                  loading={isDeleteLoading}
                  disabled={
                    isUpdateLoading || isCreateLoading || isDeleteLoading || disableDeleteButton
                  }
                  icon={<DeleteOutlined />}
                >
                  <span className={styles['extraLabel']}>Supprimer</span>
                </Button>
              </Popconfirm>
            )}
            {onUpdated && (
              <Button
                type="primary"
                icon={<SaveFilled />}
                onClick={onUpdated}
                loading={isUpdateLoading}
                disabled={isDeleteLoading || isCreateLoading || isUpdateLoading || isOnError}
              >
                <span className={styles['extraLabel']}>Mettre à jour</span>
              </Button>
            )}
            {onCreated && (
              <Button
                type="primary"
                icon={<CheckCircleFilled />}
                style={{ marginRight: "-0.75rem" }}
                onClick={onCreated}
                loading={isCreateLoading}
                disabled={isDeleteLoading || isUpdateLoading || isCreateLoading || isOnError}
              >
                <span className={styles['extraLabel']}>Valider</span>
              </Button>
            )}
          </Flex>
        </ConfigProvider>
      }
      tabList={tabList}
      activeTabKey={activeTabKey}
      onTabChange={onTabChange}
      tabProps={{
        size: 'middle',
      }}
      style={additionalStyles?.['card'] ?? undefined}
    >
      {children}
    </Card>
  );
};

export default FormCard;
